"use client";

import { NextIntlClientProvider, useLocale, useMessages, useTimeZone, type AbstractIntlMessages } from "next-intl";
import { useMemo, type ReactNode } from "react";

import { intlMessageFallback, intlOnError } from "@/i18n/intl-handlers";
import { mergeMessages, type Messages } from "@/shared/i18n/merge-messages";
import { setUiMessages } from "@/shared/lib/ui-translate";

/**
 * Слой сообщений семейства маршрутов (KAN-975, рычаг №2: словарь переводов).
 *
 * Основной словарь приходит из `root-content-ru/en` и содержит только то, что нужно оболочке и
 * большинству страниц. Группы, нужные ровно одному семейству (сейчас — админке), лежат в
 * `messages/generated/<локаль>.<семейство>.json` и подгружаются вместе с кодом самого семейства.
 * Слой берёт сообщения родительского провайдера, доливает к ним сообщения семейства и открывает
 * вложенный провайдер: хуки `useTranslations` внутри видят и то, и другое.
 *
 * Что слой обязан повторить за корневым провайдером: вложенный `NextIntlClientProvider` ничего не
 * наследует кроме контекста — локаль, часовой пояс и обработчики ошибок передаются заново.
 *
 * `translateUi` вне React читает глобальную регистрацию (`setUiMessages`); слой обновляет её
 * смёрженным словарём, как корень делает это основным. Регистрация идёт во время рендера: так
 * же, как в `RootContent`, это идемпотентно, а ключи нужны уже при первом рендере детей.
 */

/** Глубокое слияние словарей живёт в merge-messages.ts — им пользуется и translateUi. */
export { mergeMessages };

export function MessagesLayer({ extra, children }: { extra: Messages; children: ReactNode }) {
  const locale = useLocale();
  const timeZone = useTimeZone();
  const parent = useMessages() as Messages;
  const merged = useMemo(() => mergeMessages(parent, extra), [parent, extra]);
  setUiMessages(locale, merged as AbstractIntlMessages);

  return (
    <NextIntlClientProvider
      locale={locale}
      messages={merged as AbstractIntlMessages}
      timeZone={timeZone}
      onError={intlOnError}
      getMessageFallback={intlMessageFallback}
    >
      {children}
    </NextIntlClientProvider>
  );
}
